import React, { forwardRef, useState } from 'react';
import Icon from '../AppIcon';

const Input = forwardRef(({
  type = 'text',
  label,
  placeholder,
  value,
  onChange,
  onFocus,
  onBlur,
  icon,
  iconPosition = 'left',
  error,
  success,
  disabled = false,
  required = false,
  className = '',
  id,
  name,
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);

  const inputId = id || `input-${name || Math.random().toString(36).substr(2, 9)}`;

  const handleFocus = (e) => {
    setIsFocused(true);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    if (onBlur) onBlur(e);
  };

  const baseClasses = 'flex h-10 w-full rounded-md border bg-white px-3 py-2 text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50';

  const stateClasses = error
    ? 'border-error focus:border-error focus:ring-error/30 text-error'
    : success
      ? 'border-success focus:border-success focus:ring-success/30' : 'border-slate-200 focus:border-primary-600 focus:ring-primary-600/30';

  const iconClasses = icon ? (iconPosition === 'left' ? 'pl-10' : 'pr-10') : '';

  const inputClasses = [
    baseClasses,
    stateClasses,
    iconClasses,
    className
  ].join(' ');

  return (
    <div className="space-y-2">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl font-medium text-slate-700"
        >
          {label}
          {required && <span className="text-error ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        {icon && iconPosition === 'left' && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <Icon
              name={icon}
              size={18}
              className={error ? 'text-error' : success ? 'text-success' : 'text-slate-400'}
            />
          </div>
        )}

        <input
          ref={ref}
          id={inputId}
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder}
          required={required}
          className={inputClasses}
          {...props}
        />

        {icon && iconPosition === 'right' && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <Icon
              name={icon}
              size={18}
              className={error ? 'text-error' : success ? 'text-success' : 'text-slate-400'}
            />
          </div>
        )}

        {error && !icon && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <Icon name="AlertCircle" size={18} className="text-error" />
          </div>
        )}

        {success && !icon && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <Icon name="CheckCircle" size={18} className="text-success" />
          </div>
        )}
      </div>

      {error && (
        <p className="text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl text-error mt-1">{error}</p>
      )}
    </div>
  );
});

export default Input;